import React, { useState } from 'react';
import { ScreenName, Store, CartItem, Gift as GiftType, Order, Drop, CafeList } from './types';
import { MOCK_USER, STORES, MOCK_ORDERS } from './data/mockData';
import { ToastProvider } from './components/ToastContext';
import { BottomNav } from './components/BottomNav';
// Screens
import { HomeScreen } from './components/screens/HomeScreen';
import { ExploreScreen } from './components/screens/ExploreScreen';
import { StoreProfileScreen } from './components/screens/StoreProfileScreen';
import { MenuScreen } from './components/screens/MenuScreen';
import { CheckoutScreen } from './components/screens/CheckoutScreen';
import { BillSplitScreen } from './components/screens/BillSplitScreen';
import { OrdersScreen } from './components/screens/OrdersScreen';
import { OrderDetailsScreen } from './components/screens/OrderDetailsScreen';
import { GiftingScreen } from './components/screens/GiftingScreen';
import { GiftHistoryScreen } from './components/screens/GiftHistoryScreen';
import { GiftRedeemScreen } from './components/screens/GiftRedeemScreen';
import { ProfileScreen } from './components/screens/ProfileScreen';
import { CheckInSuccessScreen } from './components/screens/CheckInSuccessScreen';
import { DropsScreen } from './components/screens/DropsScreen';
import { DropDetailScreen } from './components/screens/DropDetailScreen';
import { DropUnlockedScreen } from './components/screens/DropUnlockedScreen';
import { PlanScreen } from './components/screens/PlanScreen';
import { ScanScreen } from './components/screens/ScanScreen';
import { HostGatheringScreen } from './components/screens/HostGatheringScreen';
import { FavoritePlacesScreen } from './components/screens/FavoritePlacesScreen';
import { ListDetailScreen } from './components/screens/ListDetailScreen';
import { MixAndShareScreen } from './components/screens/MixAndShareScreen';
export function App() {
  const [currentScreen, setCurrentScreen] = useState<ScreenName>('home');
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [selectedGift, setSelectedGift] = useState<GiftType | null>(null);
  const [isImHereFlow, setIsImHereFlow] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [selectedDrop, setSelectedDrop] = useState<Drop | null>(null);
  const [unlockedDropCode, setUnlockedDropCode] = useState<string | null>(null);
  const [giftRedeemSource, setGiftRedeemSource] = useState<'gifting' | 'giftHistory'>('giftHistory');
  const [profileOpenSection, setProfileOpenSection] = useState<string | null>(null);
  const [selectedList, setSelectedList] = useState<CafeList | null>(null);
  // Bill split data
  const [billSplitData, setBillSplitData] = useState<{
    participants: Array<{
      id: string;
      name: string;
      items: Array<{
        name: string;
        price: number;
      }>;
      total: number;
    }>;
    orderTotal: number;
    tax: number;
  } | null>(null);
  // Gifting context state
  const [giftingContext, setGiftingContext] = useState<{
    storeId: string | null;
    storeName: string | null;
    storeCode: string | null;
  }>({
    storeId: null,
    storeName: null,
    storeCode: null
  });
  const navigateTo = (screen: ScreenName, data?: any) => {
    window.scrollTo(0, 0);
    if (screen === 'giftRedeem' && data) {
      setSelectedGift(data.gift || data);
      setGiftRedeemSource(data.from || 'giftHistory');
    }
    if (screen === 'profile' && data?.openSection) {
      setProfileOpenSection(data.openSection);
    } else {
      setProfileOpenSection(null);
    }
    setCurrentScreen(screen);
  };
  // Peek Inside handler - browse mode (no loyalty points)
  const handlePeekInside = (store: Store) => {
    setSelectedStore(store);
    setIsImHereFlow(false);
    navigateTo('storeProfile');
  };
  // I'm Here handler - loyalty mode (awards points, shows success screen)
  const handleImHere = (store: Store) => {
    setSelectedStore(store);
    setIsImHereFlow(true);
    // Award loyalty points here (in real app, this would be an API call)
    navigateTo('checkInSuccess');
  };
  // Legacy store click handler (for compact cards without buttons)
  const handleStoreClick = (store: Store) => {
    setSelectedStore(store);
    setIsImHereFlow(false);
    navigateTo('storeProfile');
  };
  // Gift handler - navigates to gifting with pre-filled store
  const handleGiftFromStore = (storeId: string, storeName: string, storeCode: string) => {
    setGiftingContext({
      storeId,
      storeName,
      storeCode
    });
    navigateTo('gifting');
  };
  // Drop handlers
  const handleDropClick = (drop: Drop) => {
    setSelectedDrop(drop);
    navigateTo('dropDetail');
  };
  const handleUnlockDrop = () => {
    // Generate unique drop code
    const code = `#${selectedDrop?.brandName.substring(0, 4).toUpperCase()}${Math.floor(1000 + Math.random() * 9000)}`;
    setUnlockedDropCode(code);
    navigateTo('dropUnlocked');
  };
  const handleApplyDropToOrder = () => {
    // In real app, this would add the drop to cart and navigate to store menu
    // For now, just navigate back to drops
    navigateTo('drops');
  };
  const handleSendDropAsGift = () => {
    // In real app, this would navigate to gift sending flow
    // For now, just navigate back to drops
    navigateTo('drops');
  };
  const handleViewMenu = () => {
    navigateTo('menu');
  };
  const handleCheckout = (items: CartItem[]) => {
    setCartItems(items);
    navigateTo('checkout');
  };
  const handlePlaceOrder = (billSplit?: {
    participants: Array<{
      id: string;
      name: string;
      items: Array<{
        name: string;
        price: number;
      }>;
      total: number;
    }>;
    orderTotal: number;
    tax: number;
  }) => {
    // Get the active order (in real app, this would be the newly created order from API)
    const activeOrder = MOCK_ORDERS.find(o => o.status !== 'completed');
    // Don't clear cart items - keep them so user can go back to checkout
    if (billSplit) {
      // Group order - navigate to bill split screen
      setBillSplitData(billSplit);
      navigateTo('billSplit');
    } else {
      // Regular order - go directly to order details screen
      if (activeOrder) {
        setCurrentOrder(activeOrder);
        navigateTo('orderDetails');
      }
    }
  };
  const handleCheckIn = () => {
    setIsImHereFlow(true);
    navigateTo('checkInSuccess');
  };
  const renderScreen = () => {
    switch (currentScreen) {
      case 'home':
        return <HomeScreen user={MOCK_USER} onNavigate={navigateTo} onStoreClick={handleStoreClick} data-id="element-0" />;
      case 'explore':
        return <ExploreScreen onStoreClick={handleStoreClick} onPeekInside={handlePeekInside} onImHere={handleImHere} data-id="element-1" />;
      case 'storeProfile':
        return selectedStore ? <StoreProfileScreen store={selectedStore} onBack={() => {
          setIsImHereFlow(false);
          navigateTo('explore');
        }} onViewMenu={handleViewMenu} onCheckIn={handleCheckIn} onGift={handleGiftFromStore} isImHereFlow={isImHereFlow} data-id="element-2" /> : null;
      case 'menu':
        return selectedStore ? <MenuScreen store={selectedStore} onBack={() => navigateTo('storeProfile')} onCheckout={handleCheckout} data-id="element-3" /> : null;
      case 'checkout':
        return <CheckoutScreen items={cartItems} onBack={() => navigateTo('menu')} onPlaceOrder={handlePlaceOrder} data-id="element-4" />;
      case 'billSplit':
        return billSplitData && selectedStore ? <BillSplitScreen participants={billSplitData.participants} orderTotal={billSplitData.orderTotal} tax={billSplitData.tax} storeName={selectedStore.name} onBack={() => navigateTo('checkout')} data-id="element-5" /> : null;
      case 'orders':
        return <OrdersScreen data-id="element-6" />;
      case 'orderDetails':
        return currentOrder ? <OrderDetailsScreen order={currentOrder} onBack={() => navigateTo('checkout')} data-id="element-7" /> : null;
      case 'gifting':
        return <GiftingScreen onNavigate={navigateTo} prefilledStore={giftingContext} onClearPrefill={() => setGiftingContext({
          storeId: null,
          storeName: null,
          storeCode: null
        })} data-id="element-8" />;
      case 'giftHistory':
        return <GiftHistoryScreen onBack={() => navigateTo('profile')} onRedeem={gift => navigateTo('giftRedeem', {
          gift,
          from: 'giftHistory'
        })} data-id="element-9" />;
      case 'giftRedeem':
        return selectedGift ? <GiftRedeemScreen gift={selectedGift} onBack={() => navigateTo(giftRedeemSource === 'gifting' ? 'gifting' : 'giftHistory')} data-id="element-10" /> : null;
      case 'plan':
        return <PlanScreen onBack={() => navigateTo('profile')} onNavigate={navigateTo} data-id="element-11" />;
      case 'hostGathering':
        return <HostGatheringScreen onBack={() => navigateTo('profile')} onNavigate={navigateTo} data-id="element-12" />;
      case 'scan':
        return <ScanScreen onBack={() => navigateTo('home')} data-id="element-13" />;
      case 'drops':
        return <DropsScreen onBack={() => navigateTo('home')} onDropClick={handleDropClick} data-id="element-14" />;
      case 'dropDetail':
        return selectedDrop ? <DropDetailScreen drop={selectedDrop} onBack={() => navigateTo('drops')} onUnlock={handleUnlockDrop} data-id="element-15" /> : null;
      case 'dropUnlocked':
        return selectedDrop && unlockedDropCode ? <DropUnlockedScreen drop={selectedDrop} dropCode={unlockedDropCode} onApplyToOrder={handleApplyDropToOrder} onSendAsGift={handleSendDropAsGift} onClose={() => navigateTo('drops')} data-id="element-16" /> : null;
      case 'profile':
        return <ProfileScreen user={MOCK_USER} onNavigate={navigateTo} openSection={profileOpenSection} data-id="element-17" />;
      case 'checkInSuccess':
        return selectedStore ? <CheckInSuccessScreen store={selectedStore} onDismiss={() => {
          setIsImHereFlow(false);
          navigateTo('explore');
        }} onViewMenu={handleViewMenu} data-id="element-18" /> : null;
      case 'favoritePlaces':
        return <FavoritePlacesScreen onBack={() => navigateTo('profile')} onOpenList={list => {
          setSelectedList(list);
          navigateTo('listDetail');
        }} onMixAndShare={() => navigateTo('mixShare')} data-id="element-19" />;
      case 'listDetail':
        return selectedList ? <ListDetailScreen list={selectedList} onBack={() => navigateTo('favoritePlaces')} onMixAndShare={() => navigateTo('mixShare')} data-id="element-20" /> : null;
      case 'mixShare':
        return <MixAndShareScreen onBack={() => navigateTo(selectedList ? 'listDetail' : 'favoritePlaces')} data-id="element-21" />;
      default:
        return <HomeScreen user={MOCK_USER} onNavigate={navigateTo} onStoreClick={handleStoreClick} data-id="element-22" />;
    }
  };
  return <ToastProvider data-id="element-23">
      <div className="min-h-screen bg-gray-100 flex justify-center" data-id="element-24">
        <div className="w-full max-w-md bg-white shadow-2xl rounded-2xl min-h-screen relative overflow-hidden" data-id="element-25">
          {renderScreen()}

          {/* Hide BottomNav on full-screen flows */}
          {!['checkInSuccess', 'giftRedeem', 'menu', 'checkout', 'billSplit', 'orderDetails', 'dropDetail', 'dropUnlocked', 'plan', 'hostGathering', 'scan', 'favoritePlaces', 'listDetail', 'mixShare'].includes(currentScreen) && <BottomNav currentScreen={currentScreen} onNavigate={navigateTo} data-id="element-26" />}
        </div>
      </div>
    </ToastProvider>;
}